// src/pages/account/ChangePassword.js
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { BASE_URL } from '../../utils/config';
import { apiFetch } from '../../utils/apiClient';
import { validatePassword } from '../../utils/userValidation';
import './ChangePassword.css'; // New CSS file
import { useLanguage } from '../../hooks/useLanguage';

const ChangePassword = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const navigate = useNavigate();

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lang = useLanguage();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      addNotification(t('changePasswordPage.passwordsDoNotMatch'), 'error');
      return;
    }
    const passwordError = validatePassword(passwordData.newPassword);
    if (passwordError) {
      addNotification(passwordError, 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiFetch(
        `${BASE_URL}/authUser/change-password`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword,
          }),
        },
        user.token
      );

      const data = await response.json();
      if (response.ok) {
        addNotification(t('changePasswordPage.success'), 'success');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        navigate(`/${lang}/preferences`); // Go back to preferences on success
      } else {
        addNotification(t('changePasswordPage.failure', { error: data.message || t('changePasswordPage.genericError') }), 'error');
      }
    } catch (err) {
      addNotification(t('changePasswordPage.networkError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="change-password-page-container">
      <div className="change-password-container">
        <h2>{t('changePasswordPage.title')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="currentPassword">{t('changePasswordPage.currentPasswordLabel')}</label>
            <input type="password" id="currentPassword" name="currentPassword" value={passwordData.currentPassword} onChange={handleChange} required disabled={isSubmitting} autoComplete="current-password" />
          </div>
          <div className="form-group">
            <label htmlFor="newPassword">{t('changePasswordPage.newPasswordLabel')}</label>
            <input type="password" id="newPassword" name="newPassword" value={passwordData.newPassword} onChange={handleChange} required disabled={isSubmitting} autoComplete="new-password" />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('changePasswordPage.confirmPasswordLabel')}</label>
            <input type="password" id="confirmPassword" name="confirmPassword" value={passwordData.confirmPassword} onChange={handleChange} required disabled={isSubmitting} autoComplete="new-password" />
          </div>
          <div className="button-group">
            <button type="button" className="cancel-btn" onClick={() => navigate(`/${lang}/preferences`)} disabled={isSubmitting}>
              {t('changePasswordPage.cancelButton')}
            </button>
            <button type="submit" className="action-btn" disabled={isSubmitting}>
              {isSubmitting ? t('changePasswordPage.changingButton') : t('changePasswordPage.changeButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
